import React from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { NLink } from "@/i18n/I18nProvider";

const CourseDivider = ({ idx }) => (
  <div className="flex items-center justify-center gap-6" aria-hidden="true" data-testid={`course-divider-${idx}`}>
    <span className="block h-px flex-1 max-w-[160px] bg-[#C4A46A]/40" />
    <span className="block h-1.5 w-1.5 rotate-45 bg-[#C4A46A]" />
    <span className="block h-px flex-1 max-w-[160px] bg-[#C4A46A]/40" />
  </div>
);

const CourseFacts = ({ facts }) => (
  <dl className="border-t border-[#0F1B2D]/10">
    {facts.map((f) => (
      <div key={f.k} className="grid grid-cols-1 sm:grid-cols-[minmax(110px,180px)_1fr] gap-2 sm:gap-6 py-4 border-b border-[#0F1B2D]/10">
        <dt className="overline text-[#5E6470]">{f.k}</dt>
        <dd className="text-[#0F1B2D] text-[15px] leading-snug">{f.v}</dd>
      </div>
    ))}
  </dl>
);

const FeaturedCourse = ({ ce, fc, idx }) => (
  <div id={`course-${idx}`} data-testid={`featured-course-${idx}`} className="scroll-mt-40">
    <div className="flex items-center gap-3 flex-wrap">
      <span className="inline-block px-3 py-1 text-[10px] tracking-[0.28em] uppercase font-bold border border-[#C4A46A] text-[#C4A46A]" data-testid={`featured-course-badge-${idx}`}>
        {ce.featuredLabel}
      </span>
      <span className="font-cormorant italic text-[#5E6470] text-lg">{fc.partner}</span>
    </div>
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-7">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0F1B2D] leading-[1.05]">{fc.title}</h2>
        <p className="mt-4 font-cormorant italic text-2xl lg:text-3xl text-[#5E6470] leading-snug">{fc.subtitle}</p>
        <span className="gold-rule mt-8 block" />
        <p className="mt-8 text-[#5E6470] text-lg leading-relaxed max-w-2xl">{fc.d}</p>
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <a
            href={fc.registerHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`featured-course-register-${idx}`}
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#0F1B2D] text-white text-[12px] tracking-[0.24em] uppercase font-bold hover:bg-[#C4A46A] hover:text-[#0F1B2D] transition-colors"
          >
            {fc.registerCta}
            <ArrowUpRight size={16} />
          </a>
          <img src="/partners/sportrail.webp" alt="Sportrail" className="h-10 sm:h-12 w-auto opacity-90" loading="lazy" />
        </div>
      </div>
      <div className="lg:col-span-5"><CourseFacts facts={fc.facts} /></div>
    </div>
  </div>
);

export const FeaturedCourses = ({ ce, featured }) => (
  <section className="bg-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 space-y-24 lg:space-y-32">
      {featured.map((fc, idx) => (
        <React.Fragment key={fc.title}>
          {idx > 0 && <CourseDivider idx={idx} />}
          <FeaturedCourse ce={ce} fc={fc} idx={idx} />
        </React.Fragment>
      ))}
    </div>
  </section>
);

export const UpcomingCourses = ({ ce }) => (
  <section className="bg-[#F4F0E8]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <div className="overline text-[#C4A46A]">{ce.upcomingLabel}</div>
      <span className="gold-rule mt-4 block" />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#0F1B2D]/10">
        {ce.upcoming.map((item, i) => (
          <article key={item.t} data-testid={`coach-ed-item-${i}`} className="bg-[#F4F0E8] p-10 lg:p-12 flex flex-col min-h-[280px]">
            <div className="font-display text-[#C4A46A] text-sm">{String(i + 1).padStart(2, "0")}</div>
            <h3 className="mt-4 font-display text-xl lg:text-2xl text-[#0F1B2D] tracking-tight leading-snug">{item.t}</h3>
            <span className="gold-rule mt-4 block" />
            <p className="mt-5 text-[#5E6470] text-[15px] leading-relaxed">{item.d}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const CoachHero = ({ ce }) => (
  <section className="bg-[#0F1B2D] text-white pt-28 sm:pt-36 pb-16 sm:pb-24 relative grain">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="overline text-[#C4A46A]">{ce.kicker}</div>
      <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">{ce.title}</h1>
      <span className="gold-rule mt-8 block" />
      <p className="mt-10 max-w-3xl text-white/80 text-lg leading-relaxed">{ce.intro}</p>
    </div>
  </section>
);

export const CoachClosing = ({ ce }) => (
  <section className="bg-[#0F1B2D] text-white">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16 sm:py-24 text-center">
      <p className="mx-auto max-w-3xl font-display text-2xl lg:text-3xl leading-snug tracking-tight">{ce.closing}</p>
      <span className="gold-rule mt-10 mx-auto block" />
      <NLink
        to="/contact"
        data-testid="coach-education-cta"
        className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#C4A46A] text-[#0F1B2D] text-[12px] tracking-[0.24em] uppercase font-bold hover:bg-[#A38755] transition-colors"
      >
        {ce.contactCta} <ArrowRight size={16} />
      </NLink>
    </div>
  </section>
);
